import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CopilotToolUsage } from "@/types";
import { SURFACE_LABEL, displayToolName, toolSurface } from "./copilotToolSurface";

const SURFACE_STYLE: Record<string, string> = {
  frontend: "border-primary/30 bg-primary/10 text-primary",
  backend: "border-accent/30 bg-accent/10 text-accent",
  gateway: "border-border bg-muted text-muted-foreground",
};

/** Anything at or above this failure share is worth a PO's attention. */
const FAILURE_ALERT_THRESHOLD = 0.2;

export function CopilotToolTable({ tools }: { tools: CopilotToolUsage[] }) {
  if (!tools.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No tool calls in this window.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="pb-2 pr-4 font-medium">Tool</th>
            <th className="pb-2 pr-4 font-medium">Runs on</th>
            <th className="pb-2 pr-4 text-right font-medium">Calls</th>
            <th className="pb-2 pr-4 text-right font-medium">Failed</th>
            <th className="pb-2 text-right font-medium">Failure rate</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((tool) => {
            const surface = toolSurface(tool.toolName);
            // missingResults are runs that died before returning anything —
            // count them as failures so a crashing tool cannot look healthy.
            const failed = tool.failures + tool.missingResults;
            const rate = tool.calls > 0 ? failed / tool.calls : 0;
            const alerting = rate >= FAILURE_ALERT_THRESHOLD;

            return (
              <tr key={tool.toolName || "(unknown)"} className="border-b last:border-0">
                <td className="py-2 pr-4 font-mono text-xs">
                  {displayToolName(tool.toolName)}
                </td>
                <td className="py-2 pr-4">
                  <Badge variant="outline" className={cn("text-xs", SURFACE_STYLE[surface])}>
                    {SURFACE_LABEL[surface]}
                  </Badge>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">{tool.calls}</td>
                <td className="py-2 pr-4 text-right tabular-nums">{failed}</td>
                <td
                  className={cn(
                    "py-2 text-right tabular-nums",
                    alerting && "font-semibold text-destructive",
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {alerting && <AlertTriangle className="h-3.5 w-3.5" />}
                    {Math.round(rate * 100)}%
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
